import React, { useEffect, useState } from 'react'
import SideBar from '../components/SideBar'
import DetailsHeaderBar from '../components/DetailsHeaderBar'
import { useNavigate } from 'react-router-dom';
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { categoryService } from '../services/categoryService';

function CreateSubCategory() {
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    const [images, setImages] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);

    const [subCategoryName, setSubCategoryName] = useState("");
    const [slug, setSlug] = useState("");
    const [description, setDescription] = useState("");
    const [keywordInput, setKeywordInput] = useState("");

    const statusOptions = [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
    ];

    const [status, setStatus] = useState(statusOptions[0]);
    const [category, setCategory] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [categoryMaster, setCategoryMaster] = useState(null);
    const [categoryMasterOptions, setCategoryMasterOptions] = useState([]);

    // Fetch categories for dropdowns on component mount
    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setFetching(true);
            const categoriesResponse = await categoryService.getAllCategories({ per_page: 100 });
            if (categoriesResponse.categories) {
                // For category dropdown (main categories)
                const options = categoriesResponse.categories.map(cat => ({
                    value: cat.id,
                    label: cat.name
                }));
                setCategoryOptions(options);
                
                // For category master dropdown (same data for now)
                const masterOptions = categoriesResponse.categories.map(cat => ({
                    value: cat.id,
                    label: cat.name
                }));
                setCategoryMasterOptions(masterOptions);
            }
        } catch (error) {
            console.error('Failed to fetch categories:', error);
            alert('Failed to load categories. Please try again.');
        } finally {
            setFetching(false);
        }
    };

    const validateBasicDetails = () => {
        if (!subCategoryName.trim()) {
            alert("Sub category name is required");
            return false;
        }
        if (!category) {
            alert("Category is required");
            return false;
        }
        return true;
    };

    const validateStep = () => {
        if (step === 1) return true;
        if (step === 2) {
            return subCategoryName.trim() !== "" && category !== null;
        }
        return true;
    };

    const nextStep = () => {
        if (step === 2) {
            const isValid = validateBasicDetails();
            if (!isValid) return;
        }
        if (validateStep()) setStep((prev) => Math.min(prev + 1, 2));
        else alert("Please fill all required fields before proceeding.");
    };
    
    const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

    const steps = [
        { label: "Sub category image", sub: "Enter sub category image details" },
        { label: "Basic details", sub: "Enter sub category basic details" },
    ];


    const handleImagesChange = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map((file) => ({
            file,
            preview: URL.createObjectURL(file),
        }));
        setImages(newImages); // Replace existing images with new ones
    };

    const handleCreateSubCategory = async () => {
        if (!validateStep()) {
            alert("Please fill all required fields before creating the sub category.");
            return;
        }

        setLoading(true);
        try {
            // Prepare the data payload
            const payload = {
                name: subCategoryName,
                slug: slug || subCategoryName.toLowerCase().replace(/\s+/g, '-'),
                description: description,
                status: status.value,
                category_id: category?.value || '',
                category_master_id: categoryMaster?.value || category?.value || '',
            };

            // Get the image file if exists
            const imageFile = images.length > 0 ? images[0].file : null;

            const response = await categoryService.createSubcategory(payload, imageFile);
            
            setOpenSnackbar(true);
            setTimeout(() => {
                navigate(`/subcategory-details/${response.subcategory?.id || response.id}`);
            }, 2000);
            
        } catch (error) {
            console.error('Create subcategory error:', error);
            console.error('Error details:', error.response?.data);
            alert(error.response?.data?.message || error.response?.data?.errors?.name?.[0] || 'Failed to create sub category');
        } finally {
            setLoading(false);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setOpenSnackbar(false);
    };

    if (fetching) {
        return (
            <div className="manage-member-layout">
                <SideBar />
                <div className="product-details-main">
                    <DetailsHeaderBar
                        title="Create New Sub Category"
                        subtitle="You can create a new sub category to organize your products"
                        breadcrumbs={[
                            {label: "Home", path: "/manage-member"},
                            {label: "Manage Sub Category", path: "/manage-subcategory"},
                            {label: "Create New Sub Category"}
                        ]}
                    />
                    <div style={{textAlign: 'center', padding: '50px'}}>Loading categories...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-member-layout">
            <SideBar />
            <div className="product-details-main">
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MuiAlert
                        severity="success"
                        icon={<span style={{ fontWeight: "bold", fontSize: "18px", color:'rgba(51, 178, 51, 1' }}>✔</span>}
                        sx={{
                            backgroundColor: "transparent",
                            backdropFilter: "blur(8px)",
                            color: "#000",
                            border: "2px solid rgba(51, 178, 51, 1)",
                            borderRadius: "10px",
                            boxShadow: "none",
                            fontSize: "15px",
                            padding: "8px 12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            height: '25px',
                            width: '100%',
                            textAlign: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        Sub category created successfully!
                    </MuiAlert>
                </Snackbar>

                <DetailsHeaderBar
                    title="Create New Sub Category"
                    subtitle="You can create a new sub category to organize your products"
                    breadcrumbs={[
                        {label: "Home", path: "/manage-member"},
                        {label: "Manage Sub Category", path: "/manage-subcategory"},
                        {label: "Create New Sub Category"}
                    ]}
                />

                <div className="multi-form-container">
                    <div className="progress-bar">
                        {steps.map((item, i) => {
                            const stepNum = i + 1;
                            const isActive = step === stepNum;
                            const isCompleted = step > stepNum;
                            return (
                                <div key={i} className="progress-step">
                                    <div className={`circle ${isCompleted ? "completed" : isActive ? "active" : ""}`}>
                                        {isCompleted ? "✓" : stepNum}
                                    </div>
                                    <div className="step-text">
                                        <p className={`step-label ${isActive ? "active-label" : ""}`}>
                                            {item.label}
                                        </p>
                                        <p className={`step-subtext ${isActive ? "active-sub" : ""}`}>
                                            {item.sub}
                                        </p>
                                    </div>
                                    {i < steps.length - 1 && (
                                        <div className={`line ${isCompleted ? "line-filled" : "line-empty"}`}></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Step content */}
                    <div className="step-content">
                        {step === 1 && (
                            <div>
                                <h3>Sub Category Image Details</h3>
                                <hr/>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Sub Category Image <span className="required"></span></label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImagesChange}
                                            style={{maxWidth:"100%"}}
                                            disabled={loading}
                                        />
                                        <small>Upload an image for the sub category</small>

                                        <div style={{ display: "flex", gap: 10, marginTop: 10}}>
                                            {images.map((img, index) => (
                                                <div 
                                                    key={index}
                                                    style={{
                                                        position: "relative",
                                                        height: 120,
                                                    }}
                                                >
                                                    <img 
                                                        src={img.preview} 
                                                        alt='subcategory' 
                                                        onClick={() => setPreviewImage(img.preview)}
                                                        style={{
                                                            width: 120,
                                                            height: 120,
                                                            objectFit: "cover",
                                                            borderRadius: 6,
                                                            display: "block",
                                                            marginBottom: 6,
                                                            cursor:"pointer",
                                                        }} 
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setImages([])}
                                                        style={{
                                                            position: "absolute",
                                                            top: 4,
                                                            right: 4,
                                                            background: "#e53935",
                                                            color: "#fff",
                                                            border: "none",
                                                            borderRadius: "50%",
                                                            width: 24,
                                                            height: 24,
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontWeight: "bold",
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        x
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>       
                        )}
                        {step === 2 && (
                            <div>
                                <h3>Basic Details</h3>
                                <hr/>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Sub category name <span className="required">*</span></label>
                                        <input 
                                            type="text" 
                                            value={subCategoryName}
                                            onChange={(e) => setSubCategoryName(e.target.value)}
                                            placeholder="Enter sub category name" 
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Sub category slug</label>
                                        <input 
                                            type="text" 
                                            value={slug}
                                            onChange={(e) => setSlug(e.target.value)}
                                            placeholder="Enter sub category slug" 
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Category <span className="required">*</span></label>
                                        <Select
                                            options={categoryOptions}
                                            value={category}
                                            onChange={setCategory}
                                            placeholder="Select category"
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                        color: isSelected ? '#000' : '#000',
                                                        cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Category master</label>
                                        <Select
                                            options={categoryMasterOptions}
                                            value={categoryMaster}
                                            onChange={setCategoryMaster}
                                            placeholder="Select category master"
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                        color: isSelected ? '#000' : '#000',
                                                        cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Status <span className="required">*</span></label>
                                        <Select
                                            options={statusOptions}
                                            value={status}
                                            onChange={setStatus}
                                            placeholder={statusOptions[0].label}
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                        color: isSelected ? '#000' : '#000',
                                                        cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                    </div>
                                    
                                    <div className="form-group" style={{gridColumn: 'span 2'}}>
                                        <label>Description</label>
                                        <textarea
                                            className="note-textarea"
                                            rows="3"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="enter description here"
                                            style={{padding:"10px"}}
                                            disabled={loading}
                                        />  
                                    </div>

                                </div>            
                            </div>        
                        )}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="btn-row">
                        {step > 1 && (
                            <button className="btn prev" onClick={prevStep} disabled={loading}>
                                Previous
                            </button>
                        )}
                        {step < 2 && (
                            <button 
                                className="btn next" 
                                onClick={nextStep}
                                disabled={!validateStep() || loading}
                                style={{
                                    opacity: validateStep() ? 1 : 0.6,
                                    cursor: validateStep() ? 'pointer' : 'not-allowed'
                                }}
                            >
                                Next
                            </button>
                        )}
                        {step === 2 && (
                            <button 
                                className="btn next" 
                                onClick={handleCreateSubCategory}
                                disabled={!validateStep() || loading}
                                style={{
                                    opacity: validateStep() ? 1 : 0.6,
                                    cursor: validateStep() ? 'pointer' : 'not-allowed'
                                }}
                            >
                                {loading ? 'Creating...' : 'Create Sub Category'}
                            </button>
                        )}
                    </div>
                
                    {previewImage && (
                        <div
                            onClick={() => setPreviewImage(null)}
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100vw",
                                height: "100vh",
                                background: "rgba(0,0,0,0.8)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 9999,
                            }}
                        >
                            <div style={{ position: "relative" }}>
                                <img
                                    src={previewImage}
                                    alt="Preview"
                                    style={{
                                        maxWidth: "90vw",
                                        maxHeight: "90vh",
                                        borderRadius: 10,
                                    }}
                                />
                                <button
                                    onClick={() => setPreviewImage(null)}
                                    style={{
                                        position: "absolute",
                                        top: -10,
                                        right: -10,
                                        background: "#e53935",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "50%",
                                        width: 30,
                                        height: 30,
                                        cursor: "pointer",
                                        fontSize: 16,
                                        fontWeight: "bold",
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    )}
                </div>  
            </div>
        </div>
    )
}

export default CreateSubCategory